import { useState, type FormEvent } from 'react';
import { useInviteMember, useTenantMembers } from '@/lib/api/tenants';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import type { MemberRole } from '@/lib/api/types';

const roles: { value: MemberRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

export function TeamPage() {
  const { data, isLoading, error } = useTenantMembers();
  const invite = useInviteMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('member');

  function handleInvite(e: FormEvent) {
    e.preventDefault();
    invite.mutate(
      { email, role },
      {
        onSuccess: () => setEmail(''),
      },
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Card title="Team members" description="People with access to this workspace.">
          <QueryState
            isLoading={isLoading}
            error={error}
            isEmpty={!data || data.length === 0}
            emptyMessage="No members yet."
            loadingLabel="Loading members"
            skeleton={<SkeletonRows rows={4} />}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-600">
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 font-medium">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.map((member) => (
                    <tr key={member.user_id}>
                      <td className="py-2 pr-4 text-gray-900">{member.email}</td>
                      <td className="py-2 pr-4 text-gray-600">{member.name ?? '-'}</td>
                      <td className="py-2 capitalize text-gray-600">{member.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </QueryState>
        </Card>
      </div>

      <div>
        <Card title="Invite member">
          <form onSubmit={handleInvite} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              required
            />
            <Select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>

            {invite.error && (
              <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {(invite.error as Error).message}
              </p>
            )}
            {invite.isSuccess && (
              <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                Invitation sent.
              </p>
            )}

            <Button type="submit" disabled={invite.isPending} className="w-full">
              {invite.isPending ? 'Sending...' : 'Send invite'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
